import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { RouterOutlet } from '@angular/router';
import { varCSS } from '@app/core/utils/other-utils';
import { IRestaurant } from '@models/IRestaurant';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpCriteria } from '@paella-front/ngx-super-http-client';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { of } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { environment } from 'src/environments';

import { ConfigService } from './core/services/config.service';
import { OauthService } from './core/services/oauth.service';
import { PlatformService } from './core/services/platform.service';
import { RestaurantService } from './core/services/restaurant.service';
import { AccessibilityService } from './modules/accesibility/services/accessibility.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [RouterOutlet, NgxSpinnerModule, TranslateModule],
})
class AppComponent implements OnInit {
  readonly spinnerColor = varCSS('bs-primary');

  restaurant: IRestaurant;
  url: string;
  private imageUrl: string;
  private token = '';

  constructor(
    private translate: TranslateService,
    private restaurantService: RestaurantService,
    private oauthService: OauthService,
    private $config: ConfigService,
    private messaging: AngularFireMessaging,
    private analytics: AngularFireAnalytics,
    private $spinner: NgxSpinnerService,
    private $platform: PlatformService,
    private $accesibility: AccessibilityService
  ) {}

  async ngOnInit(): Promise<void> {
    this.translate.setDefaultLang((await this.$config.getLang()) || 'es');

    await this.$accesibility.isLargeTextEnabled();

    this.translate.use((await this.$config.getLang()) || 'es');

    this.$config.urlToken$
      .pipe(
        filter((urlToken: string) => !!urlToken),
        tap((urlToken: string) => {
          if (urlToken) {
            this.getRestaurant(urlToken);
          }
        })
      )
      .subscribe();

    this.restaurantService.currentRestaurant$.subscribe({
      next: (restaurant: IRestaurant) => {
        if (restaurant && (!this.restaurant || this.restaurant.id !== restaurant.id)) {
          this.restaurant = restaurant;
          this.url = `${environment.domain}app/public/local/${this.restaurant.urlTokenAlias}`;
          this.generateQr();
        }
      },
      error: (error: Error) => {
        console.error(error);
      },
    });

    this.analytics.setAnalyticsCollectionEnabled(true);

    this.messaging.requestToken.subscribe({
      next: (token: string) => {
        if (token) {
          this.token = token;
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('Fetching FCM token failed: ', +error);
      },
    });
  }

  downloadQr(): void {
    const newElement = document.createElement('a');
    document.body.appendChild(newElement);
    newElement.style.display = 'none';

    const alias = this.restaurant.urlTokenAlias
      ? this.restaurant.urlTokenAlias
      : this.restaurant.urlToken;
    newElement.href = this.imageUrl;
    const name = alias;
    newElement.download = name;
    newElement.click();
  }

  private generateQr(): void {
    this.oauthService.generateQr({ text: this.url, format: 'png' }).subscribe(
      (response: any) => {
        const imageUrl = URL.createObjectURL(response);
        this.imageUrl = imageUrl;
        const element = document.querySelector('#qr') as HTMLImageElement;
        element.src = imageUrl;
      },
      (error: any) => {
        console.error(error);
      }
    );
  }

  private getRestaurant(token: string): void {
    this.$spinner.show();

    const criteria: HttpCriteria = new HttpCriteria();
    criteria._filters = [
      {
        field: 'urlToken',
        operator: '=',
        value: token,
        condition: 'OR',
      },
      {
        field: 'urlTokenAlias',
        operator: '=',
        value: token,
        condition: 'OR',
      },
    ];

    this.$platform
      .getRestaurantByToken(criteria)
      .pipe(
        tap((restaurants: IRestaurant[]) => {
          if (!!restaurants && restaurants.length > 0) {
            this.restaurantService.setRestaurant(restaurants[0]);
          }
          this.$spinner.hide();
        }),
        catchError(() => {
          this.$spinner.hide();
          return of(null);
        })
      )
      .subscribe();
  }
}

export { AppComponent };
